import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, RefreshCw, Search, SlidersHorizontal, Volume2, VolumeX, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useMediaQuery } from '../hooks/useMediaQuery';
import {
  filterNotifications,
  type NotificationEvent,
  type NotificationFilters,
} from '../lib/notifications';
import { textMatchesSearch } from '../lib/utils';
import NotificationsFeed from '../components/notifications/NotificationsFeed';
import NotificationDetailPanel from '../components/notifications/NotificationDetailPanel';
import { useNotifications } from '../contexts/NotificationsContext';

const defaultFilters: NotificationFilters = {
  type: 'all',
  source: 'all',
  dateRange: 'last7days',
  readState: 'all',
};

// ---------- Filter chip option definitions ----------

const typeOptions: NotificationFilters['type'][] = [
  'all',
  'NEW_RESERVATION_ONLINE',
  'NEW_RESERVATION_MANUAL',
  'CANCELLATION',
  'MODIFICATION',
  'NO_SHOW_FLAGGED',
  'CUSTOMER_ARRIVED',
  'TABLE_FREED',
];

const typeLabels: Record<NotificationFilters['type'], string> = {
  all: 'Όλες',
  NEW_RESERVATION_ONLINE: 'Online κρατήσεις',
  NEW_RESERVATION_MANUAL: 'Νέες κρατήσεις',
  CANCELLATION: 'Ακυρώσεις',
  MODIFICATION: 'Τροποποιήσεις',
  NO_SHOW_FLAGGED: 'No-show',
  CUSTOMER_ARRIVED: 'Άφιξη πελάτη',
  TABLE_FREED: 'Απελευθέρωση',
};

const sourceOptions: NotificationFilters['source'][] = [
  'all',
  'online',
  'manual',
  'phone',
  'walk-in',
  'unknown',
];

const sourceLabels: Record<NotificationFilters['source'], string> = {
  all: 'Όλες',
  online: 'Online',
  manual: 'Χειροκίνητα',
  phone: 'Τηλέφωνο',
  'walk-in': 'Walk-in',
  unknown: 'Άγνωστο',
};

const dateRangeOptions: NotificationFilters['dateRange'][] = [
  'last7days',
  'today',
  'last30days',
  'all',
];

const dateRangeLabels: Record<NotificationFilters['dateRange'], string> = {
  today: 'Σήμερα',
  last7days: 'Τελευταίες 7ημ.',
  last30days: 'Τελευταίες 30ημ.',
  all: 'Όλες οι ημερ.',
};

const readStateOptions: NotificationFilters['readState'][] = ['all', 'unread'];

const readStateLabels: Record<NotificationFilters['readState'], string> = {
  all: 'Όλες',
  unread: 'Αδιάβαστα',
};


// ---------- Page component ----------

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    notifications,
    readIds,
    unreadCount,
    loading,
    soundEnabled,
    setSoundEnabled,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
  } = useNotifications();

  const isXL = useMediaQuery('(min-width: 1280px)');

  const [filters, setFilters] = useState<NotificationFilters>(defaultFilters);
  const [selectedNotification, setSelectedNotification] = useState<NotificationEvent | null>(null);
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [openFilterSheet, setOpenFilterSheet] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sheetDragOffset, setSheetDragOffset] = useState(0);
  const sheetDragStartRef = useRef<{ y: number; time: number } | null>(null);

  useEffect(() => {
    if (!openFilterSheet) return;

    setSheetDragOffset(0);
    const mainContent = document.querySelector('main');
    if (!mainContent) return;

    const previousOverflow = mainContent.style.overflow;
    mainContent.style.overflow = 'hidden';

    return () => {
      mainContent.style.overflow = previousOverflow;
    };
  }, [openFilterSheet]);

  const handleSheetPointerDown = (e: any) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest('button')) return;
    sheetDragStartRef.current = { y: e.clientY, time: Date.now() };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handleSheetPointerMove = (e: any) => {
    if (sheetDragStartRef.current === null) return;
    const dy = e.clientY - sheetDragStartRef.current.y;
    if (dy > 0) setSheetDragOffset(dy);
  };

  const handleSheetPointerUp = (e: any) => {
    if (sheetDragStartRef.current === null) return;
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    const dy = e.clientY - sheetDragStartRef.current.y;
    const dt = Date.now() - sheetDragStartRef.current.time;
    const velocity = dt > 0 ? dy / dt : 0;
    const closeThreshold = 80;
    const velocityThreshold = 0.3;
    if (dy > closeThreshold || velocity > velocityThreshold) {
      setOpenFilterSheet(false);
    } else {
      setSheetDragOffset(0);
    }
    sheetDragStartRef.current = null;
  };

  const filteredEvents = useMemo(() => {
    const base = filterNotifications(notifications, filters, readIds);
    const q = searchQuery.trim();
    if (!q) return base;
    return base.filter((event) => {
      const name = (event as { customerName?: string }).customerName ?? '';
      return textMatchesSearch(event.title, q) || textMatchesSearch(name, q);
    });
  }, [filters, notifications, readIds, searchQuery]);

  const todayCount = useMemo(
    () =>
      notifications.filter((event) => {
        const eventDate = new Date(event.timestamp);
        const now = new Date();
        return (
          eventDate.getFullYear() === now.getFullYear() &&
          eventDate.getMonth() === now.getMonth() &&
          eventDate.getDate() === now.getDate()
        );
      }).length,
    [notifications]
  );

  const handleSelect = (event: NotificationEvent) => {
    setSelectedNotification(event);
    markAsRead(event.id);
    if (!isXL) setMobileDetailsOpen(true);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshNotifications();
    } finally {
      setIsRefreshing(false);
    }
  };

  const isTypeFiltered = filters.type !== 'all';
  const isSourceFiltered = filters.source !== 'all';
  const isDateFiltered = filters.dateRange !== 'last7days';
  const isReadFiltered = filters.readState !== 'all';

  return (
    <div className="flex flex-col gap-6 pb-6">

      {/* ── HEADER ── */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Ειδοποιήσεις
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Σαφής εικόνα για νέες κρατήσεις.
          </p>
        </div>

        <div className="flex gap-2 shrink-0">
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            title={soundEnabled ? 'Απενεργοποίηση ήχου' : 'Ενεργοποίηση ήχου'}
            className="p-2.5 rounded-full bg-card border border-border text-muted-foreground hover:bg-accent transition-colors"
          >
            {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
          </button>

          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2.5 rounded-full bg-card border border-border text-muted-foreground hover:bg-accent transition-colors disabled:opacity-50"
          >
            <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* ── STATS 2×2 GRID ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-card border border-border p-3.5 rounded-2xl flex flex-col justify-between">
          <span className="text-muted-foreground text-[10px] font-bold uppercase tracking-wider">
            Αδιάβαστες
          </span>
          <span className="text-2xl font-bold text-orange-500 mt-0.5">{unreadCount}</span>
        </div>

        <div className="bg-card border border-border p-3.5 rounded-2xl flex flex-col justify-between">
          <span className="text-muted-foreground text-[10px] font-bold uppercase tracking-wider">
            Σήμερα
          </span>
          <span className="text-2xl font-bold text-emerald-500 mt-0.5">{todayCount}</span>
        </div>

        <div className="bg-card border border-border p-3.5 rounded-2xl flex flex-col justify-between">
          <span className="text-muted-foreground text-[10px] font-bold uppercase tracking-wider">
            Σύνολο
          </span>
          <span className="text-2xl font-bold text-blue-500 mt-0.5">{notifications.length}</span>
        </div>

        <button
          onClick={() => markAllAsRead(filteredEvents.map((e) => e.id))}
          className="bg-emerald-500/10 border border-emerald-500/20 p-3.5 rounded-2xl flex flex-row justify-center items-center gap-2 text-emerald-500 hover:bg-emerald-500/20 transition-colors active:scale-[0.98]"
          title="Σήμανση όλων ως αναγνωσμένα"
        >
          <Check size={18} />
          <span className="text-sm font-semibold">Διαβασμένες</span>
        </button>
      </div>

      {/* ── FILTERS: MOBILE BUTTON + DESKTOP TOOLBAR ── */}
      {/* Mobile: full-width button that opens the bottom sheet */}
      <div className="md:hidden">
        <button
          onClick={() => setOpenFilterSheet(true)}
          className="w-full flex items-center justify-center gap-2 bg-primary/10 border border-primary/30 text-primary px-4 py-3 rounded-2xl text-sm font-semibold hover:bg-primary/15 transition-colors active:scale-[0.98]"
          title="Άνοιγμα φίλτρων"
        >
          <SlidersHorizontal size={18} />
          Φίλτρα
          {(isTypeFiltered || isSourceFiltered || isDateFiltered || isReadFiltered) && (
            <span className="ml-auto inline-flex items-center justify-center w-5 h-5 bg-primary text-primary-foreground text-xs font-bold rounded-full">
              {[isTypeFiltered, isSourceFiltered, isDateFiltered, isReadFiltered].filter(Boolean).length}
            </span>
          )}
        </button>
      </div>

      {/* Desktop / tablet: compact filter toolbar with dropdowns + search */}
      <div className="hidden md:flex items-center justify-between gap-3 min-w-0">
        <div className="flex items-center gap-2 shrink-0">
          <span className="hidden lg:block text-xs font-semibold uppercase tracking-wider text-muted-foreground shrink-0">
            Φίλτρα:
          </span>

          {/* Type dropdown */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-card border border-border/70 text-sm text-muted-foreground hover:bg-accent transition-colors whitespace-nowrap min-h-[44px]"
              >
                <span className="font-medium">
                  Τύπος:{' '}
                  <span className="text-foreground font-semibold">
                    {typeLabels[filters.type]}
                  </span>
                </span>
                <span className="text-[11px] opacity-70">▼</span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="start"
              sideOffset={8}
              className="w-64 p-2 space-y-1"
            >
              {typeOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setFilters((f) => ({ ...f, type: option }))}
                  className={`flex w-full items-center justify-between px-3 py-2.5 rounded-lg text-sm min-h-[44px] ${
                    filters.type === option
                      ? 'bg-primary/10 text-primary font-semibold'
                      : 'text-foreground hover:bg-accent'
                  }`}
                >
                  <span>{typeLabels[option]}</span>
                  {filters.type === option && <Check size={15} className="text-primary shrink-0" />}
                </button>
              ))}
            </PopoverContent>
          </Popover>

          {/* Source dropdown */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-card border border-border/70 text-sm text-muted-foreground hover:bg-accent transition-colors whitespace-nowrap min-h-[44px]"
              >
                <span className="font-medium">
                  Πηγή:{' '}
                  <span className="text-foreground font-semibold">
                    {sourceLabels[filters.source]}
                  </span>
                </span>
                <span className="text-[11px] opacity-70">▼</span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="start"
              sideOffset={8}
              className="w-64 p-2 space-y-1"
            >
              {sourceOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setFilters((f) => ({ ...f, source: option }))}
                  className={`flex w-full items-center justify-between px-3 py-2.5 rounded-lg text-sm min-h-[44px] ${
                    filters.source === option
                      ? 'bg-primary/10 text-primary font-semibold'
                      : 'text-foreground hover:bg-accent'
                  }`}
                >
                  <span>{sourceLabels[option]}</span>
                  {filters.source === option && <Check size={15} className="text-primary shrink-0" />}
                </button>
              ))}
            </PopoverContent>
          </Popover>

          {/* Time / date range dropdown */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-card border border-border/70 text-sm text-muted-foreground hover:bg-accent transition-colors whitespace-nowrap min-h-[44px]"
              >
                <span className="font-medium">
                  Χρόνος:{' '}
                  <span className="text-foreground font-semibold">
                    {dateRangeLabels[filters.dateRange]}
                  </span>
                </span>
                <span className="text-[11px] opacity-70">▼</span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="start"
              sideOffset={8}
              className="w-64 p-2 space-y-1"
            >
              {dateRangeOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setFilters((f) => ({ ...f, dateRange: option }))}
                  className={`flex w-full items-center justify-between px-3 py-2.5 rounded-lg text-sm min-h-[44px] ${
                    filters.dateRange === option
                      ? 'bg-primary/10 text-primary font-semibold'
                      : 'text-foreground hover:bg-accent'
                  }`}
                >
                  <span>{dateRangeLabels[option]}</span>
                  {filters.dateRange === option && <Check size={15} className="text-primary shrink-0" />}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        </div>

        {/* Search input */}
        <div className="relative min-w-0 w-32 md:w-40 lg:w-56 xl:w-72 shrink">
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Αναζήτηση…"
            className={`w-full rounded-full bg-card border border-border/70 pl-8 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary/60 min-h-[44px] ${
              searchQuery ? 'pr-9' : 'pr-3'
            }`}
          />
          {searchQuery.length > 0 && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
              aria-label="Καθαρισμός αναζήτησης"
              title="Καθαρισμός"
            >
              <X size={14} strokeWidth={2.25} aria-hidden />
            </button>
          )}
        </div>
      </div>

      {/* ── NOTIFICATIONS FEED ── */}
      {loading ? (
        <div className="rounded-2xl border border-border bg-card p-10 text-center text-muted-foreground">
          Φόρτωση ειδοποιήσεων…
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-4">
          <NotificationsFeed
            events={filteredEvents}
            readIds={readIds}
            onSelect={handleSelect}
          />

          {isXL && (
            <NotificationDetailPanel
              notification={selectedNotification}
              isRead={selectedNotification ? readIds.has(selectedNotification.id) : true}
              onViewReservation={(notification) => {
                if (!notification) return;
                navigate('/reservations', {
                  state: {
                    fromNotification: true,
                    targetReservationId: notification.reservationId,
                    targetDate: notification.reservationDate,
                    targetTime: notification.reservationTime,
                    targetShiftId: notification.shiftId,
                  },
                });
              }}
            />
          )}
        </div>
      )}

      {/* ── DETAIL DIALOG FOR < 1280px (mobile + tablet) ── */}
      {!isXL && (
        <Dialog open={mobileDetailsOpen} onOpenChange={setMobileDetailsOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Λεπτομέρειες ειδοποίησης</DialogTitle>
          </DialogHeader>
          <NotificationDetailPanel
            notification={selectedNotification}
            isRead={selectedNotification ? readIds.has(selectedNotification.id) : true}
            onViewReservation={(notification) => {
              if (!notification) return;
              navigate('/reservations', {
                state: {
                  fromNotification: true,
                  targetReservationId: notification.reservationId,
                  targetDate: notification.reservationDate,
                  targetTime: notification.reservationTime,
                  targetShiftId: notification.shiftId,
                },
              });
            }}
          />
        </DialogContent>
      </Dialog>
      )}

      {/* ── UNIFIED FILTER BOTTOM SHEET ── */}
      {openFilterSheet && (
        <div className="fixed inset-0 z-50 flex flex-col items-end justify-end md:hidden">
          {/* Backdrop with fade animation */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
            onClick={() => setOpenFilterSheet(false)}
          />

          {/* Bottom Sheet — drag handle moves this via transform */}
          <div
            className="relative w-full bg-card border-t border-border/60 rounded-t-3xl shadow-2xl max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
            style={{
              transform: sheetDragOffset ? `translateY(${sheetDragOffset}px)` : undefined,
              transition: sheetDragOffset ? 'none' : 'transform 0.25s ease-out',
            }}
          >
            {/* Sticky header — drag anywhere here (except close button) down to close */}
            <div
              className="sticky top-0 z-10 bg-gradient-to-b from-card to-card/95 backdrop-blur-sm border-b border-border/30 px-6 pt-4 pb-4 flex flex-col gap-3 cursor-grab active:cursor-grabbing touch-none"
              onPointerDown={handleSheetPointerDown}
              onPointerMove={handleSheetPointerMove}
              onPointerUp={handleSheetPointerUp}
              onPointerCancel={handleSheetPointerUp}
              onPointerLeave={handleSheetPointerUp}
              role="button"
              tabIndex={0}
              aria-label="Σύρετε προς τα κάτω για κλείσιμο"
            >
              <div className="flex justify-between items-center pointer-events-none">
                <h2 className="text-xl font-bold">Φίλτρα</h2>
                <button
                  onClick={() => setOpenFilterSheet(false)}
                  className="p-1.5 hover:bg-accent rounded-lg transition-colors pointer-events-auto"
                  title="Κλείσιμο"
                >
                  <X size={20} className="text-muted-foreground" />
                </button>
              </div>
              <div className="w-12 h-1 bg-muted rounded-full self-center" />
            </div>

            {/* Content with sections — extra bottom padding so reset button clears bottom nav */}
            <div className="px-6 py-6 pb-24 space-y-8">
              
              {/* Type Section */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">Τύπος ειδοποίησης</h3>
                <div className="space-y-2">
                  {typeOptions.map((option) => (
                    <label
                      key={option}
                      className={`flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                        filters.type === option
                          ? 'bg-primary/10 border-primary/60 shadow-md'
                          : 'border-border/40 hover:bg-accent hover:border-primary/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="type"
                        value={option}
                        checked={filters.type === option}
                        onChange={() => setFilters((f) => ({ ...f, type: option }))}
                        className="w-4 h-4 accent-blue-400"
                      />
                      <span className={`text-sm transition-colors ${
                        filters.type === option ? 'font-bold text-primary' : 'font-medium text-foreground'
                      }`}>
                        {typeLabels[option]}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

              {/* Source Section */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">Πηγή ειδοποίησης</h3>
                <div className="space-y-2">
                  {sourceOptions.map((option) => (
                    <label
                      key={option}
                      className={`flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                        filters.source === option
                          ? 'bg-primary/10 border-primary/60 shadow-md'
                          : 'border-border/40 hover:bg-accent hover:border-primary/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="source"
                        value={option}
                        checked={filters.source === option}
                        onChange={() => setFilters((f) => ({ ...f, source: option }))}
                        className="w-4 h-4 accent-blue-400"
                      />
                      <span className={`text-sm transition-colors ${
                        filters.source === option ? 'font-bold text-primary' : 'font-medium text-foreground'
                      }`}>
                        {sourceLabels[option]}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

              {/* Date Range Section */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">Περίοδος</h3>
                <div className="space-y-2">
                  {dateRangeOptions.map((option) => (
                    <label
                      key={option}
                      className={`flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                        filters.dateRange === option
                          ? 'bg-primary/10 border-primary/60 shadow-md'
                          : 'border-border/40 hover:bg-accent hover:border-primary/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="dateRange"
                        value={option}
                        checked={filters.dateRange === option}
                        onChange={() => setFilters((f) => ({ ...f, dateRange: option }))}
                        className="w-4 h-4 accent-blue-400"
                      />
                      <span className={`text-sm transition-colors ${
                        filters.dateRange === option ? 'font-bold text-primary' : 'font-medium text-foreground'
                      }`}>
                        {dateRangeLabels[option]}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

              {/* Read State Section */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">Κατάσταση ανάγνωσης</h3>
                <div className="space-y-2">
                  {readStateOptions.map((option) => (
                    <label
                      key={option}
                      className={`flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                        filters.readState === option
                          ? 'bg-primary/10 border-primary/60 shadow-md'
                          : 'border-border/40 hover:bg-accent hover:border-primary/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="readState"
                        value={option}
                        checked={filters.readState === option}
                        onChange={() => setFilters((f) => ({ ...f, readState: option }))}
                        className="w-4 h-4 accent-blue-400"
                      />
                      <span className={`text-sm transition-colors ${
                        filters.readState === option ? 'font-bold text-primary' : 'font-medium text-foreground'
                      }`}>
                        {readStateLabels[option]}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Reset button */}
              <button
                onClick={() => {
                  setFilters(defaultFilters);
                  setOpenFilterSheet(false);
                }}
                className="w-full mt-8 py-3 rounded-xl border border-border/40 text-muted-foreground hover:text-foreground hover:bg-accent text-sm font-medium transition-colors"
              >
                Επαναφορά φίλτρων
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
